import { useMemo, useState, type ChangeEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { useAuth } from '@/stores/auth';
import { callVerifyStudent } from '@/lib/callables';
import { listStudentInstitutions, matchStudentEmailDomain, verificationRejectionMessage } from '@makhe/shared';
import { Button } from '@/components/ui/Button';
import { Card, LoadingSkeleton } from '@/components/ui/Card';
import { Select } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { signOutUser } from '@/lib/signOut';

const PHOTO_TIPS = [
  'Lay the card flat and fill the frame with it',
  'Avoid glare — tilt slightly if you see reflections',
  'Keep the bottom line with your student number in focus',
  'Use bright, even lighting (not direct overhead flash)',
] as const;

const INSTITUTION_OPTIONS = [
  { value: '', label: 'Select your institution' },
  ...listStudentInstitutions().map((name) => ({ value: name, label: name })),
];

export default function VerifyStudentPage() {
  const { firebaseUser, userDoc } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [institution, setInstitution] = useState('');

  const email = firebaseUser?.email ?? '';
  const matched = useMemo(() => matchStudentEmailDomain(email), [email]);
  const needsInstitution = !matched;

  const canSubmitCard = useMemo(() => {
    if (!file) return false;
    if (needsInstitution && !institution) return false;
    return true;
  }, [file, needsInstitution, institution]);

  if (!firebaseUser) return <Navigate to="/auth/sign-in" replace />;
  if (!userDoc) return <LoadingSkeleton className="m-6 h-40" />;

  const status = userDoc.studentVerification?.status ?? 'unverified';
  const reviewState = userDoc.studentVerification?.autoReviewState;

  const onAutoVerify = async () => {
    setSubmitting(true);
    try {
      await callVerifyStudent({ method: 'email_domain' });
      toast.success('Verified ✓');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onUploadCard = async () => {
    if (!file) return toast.error('Pick a student card image.');
    if (needsInstitution && !institution) {
      return toast.error('Select your institution.');
    }
    setSubmitting(true);
    try {
      const path = `verification/${firebaseUser.uid}/${Date.now()}-${file.name}`;
      const r = ref(storage, path);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      await callVerifyStudent({
        method: 'document_upload',
        documentURL: url,
        ...(needsInstitution ? { institution } : {}),
      });
      toast.success("Card received — we'll email you the outcome shortly.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'verified') {
    return (
      <section className="mx-auto max-w-xl px-6 py-10 text-center">
        <h1 className="font-display text-3xl">You're verified ✓</h1>
        <p className="mt-2 text-makhe-muted">
          Verified at {userDoc.studentVerification?.institution ?? 'your institution'}.
        </p>
        <Link to="/feed" className="btn-primary mt-6 inline-block">
          Go to feed
        </Link>
        <VerifyStudentFooter />
      </section>
    );
  }

  if (status === 'pending') {
    const processing = reviewState === 'processing' || reviewState === 'queued';
    return (
      <section className="mx-auto max-w-xl px-6 py-10 text-center">
        <h1 className="font-display text-3xl">
          {processing ? 'Checking your card…' : 'Under review'}
        </h1>
        <p className="mt-2 text-makhe-muted">
          {processing
            ? "We're reading your student card now. You'll get an email with the outcome in a minute or two."
            : "We're reviewing your card. You'll get an email when there's an update."}
        </p>
        <Link to="/feed" className="mt-6 inline-block text-sm text-makhe-primary">
          Browse the feed while you wait
        </Link>
        <VerifyStudentFooter />
      </section>
    );
  }

  const cardUpload = (
    <CardUploadForm
      needsInstitution={needsInstitution}
      institution={institution}
      onInstitutionChange={setInstitution}
      file={file}
      onFileChange={setFile}
      onSubmit={onUploadCard}
      submitting={submitting}
      canSubmit={canSubmitCard}
      submitLabel={status === 'rejected' || status === 'needs_info' ? 'Upload new card' : 'Submit card'}
    />
  );

  if (status === 'needs_info') {
    return (
      <section className="mx-auto max-w-xl px-6 py-8">
        <h1 className="font-display text-3xl">We need a clearer photo</h1>
        <p className="mt-2 text-makhe-muted">
          {userDoc.studentVerification?.adminNote ||
            'Please upload a clearer photo of your student card. Lay it flat, fill the frame, and keep the student number in focus.'}
        </p>
        <div className="mt-6">{cardUpload}</div>
        <VerifyStudentFooter />
      </section>
    );
  }

  if (status === 'rejected') {
    const rejectionDetail = verificationRejectionMessage(
      userDoc.studentVerification?.rejectionCode,
      userDoc.studentVerification?.rejectionMessage,
    );
    return (
      <section className="mx-auto max-w-xl px-6 py-8">
        <h1 className="font-display text-3xl">Try again</h1>
        <p className="mt-2 text-makhe-muted">{rejectionDetail}</p>
        <div className="mt-6">{cardUpload}</div>
        <VerifyStudentFooter />
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-xl px-6 py-8">
      <h1 className="font-display text-3xl">Verify your student status</h1>
      <p className="mt-2 text-makhe-muted">
        Both buyers and sellers need to be verified students.
      </p>

      {matched ? (
        <Card className="mt-6">
          <h2 className="font-display text-lg">Quick verify</h2>
          <p className="mt-1 text-sm text-makhe-ink/70">
            Your email looks like a student domain at <strong>{matched.institution}</strong>. Tap to verify
            instantly.
          </p>
          <Button onClick={onAutoVerify} loading={submitting} className="mt-4">
            Verify with my email
          </Button>
        </Card>
      ) : (
        <Card className="mt-6">
          <h2 className="font-display text-lg">Using a personal email?</h2>
          <p className="mt-1 text-sm text-makhe-ink/70">
            Your email <code>{email}</code> isn't a student address. Pick your institution and upload your
            student card — we'll verify it automatically.
          </p>
        </Card>
      )}

      <div className="mt-4">{cardUpload}</div>
      <VerifyStudentFooter />
    </section>
  );
}

function VerifyStudentFooter() {
  return (
    <p className="mt-8 text-center">
      <button
        type="button"
        onClick={() => void signOutUser()}
        className="text-sm text-makhe-muted hover:text-makhe-ink"
      >
        Sign out
      </button>
    </p>
  );
}

function CardUploadForm({
  needsInstitution,
  institution,
  onInstitutionChange,
  file,
  onFileChange,
  onSubmit,
  submitting,
  canSubmit,
  submitLabel,
}: {
  needsInstitution: boolean;
  institution: string;
  onInstitutionChange: (v: string) => void;
  file: File | null;
  onFileChange: (f: File | null) => void;
  onSubmit: () => void;
  submitting: boolean;
  canSubmit: boolean;
  submitLabel: string;
}) {
  return (
    <Card className="space-y-4">
      <h2 className="font-display text-lg">Upload student card</h2>
      <p className="text-sm text-makhe-ink/70">
        We'll scan your card automatically and email you the outcome with a link to log in.
      </p>
      {needsInstitution && (
        <Select
          label="Institution"
          value={institution}
          onChange={(e) => onInstitutionChange(e.target.value)}
          options={INSTITUTION_OPTIONS}
        />
      )}
      <PhotoTips />
      <FilePicker file={file} onChange={onFileChange} />
      <Button onClick={onSubmit} loading={submitting} disabled={!canSubmit}>
        {submitLabel}
      </Button>
    </Card>
  );
}

function PhotoTips() {
  return (
    <div className="rounded-2xl bg-makhe-line/30 px-4 py-3">
      <p className="text-sm font-medium text-makhe-ink/80">Photo tips</p>
      <ul className="mt-2 space-y-1.5 text-sm text-makhe-ink/65">
        {PHOTO_TIPS.map((tip) => (
          <li key={tip} className="flex gap-2">
            <span className="text-makhe-muted" aria-hidden>
              •
            </span>
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FilePicker({
  file,
  onChange,
}: {
  file: File | null;
  onChange: (f: File | null) => void;
}) {
  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.files?.[0] ?? null);
    e.target.value = '';
  };

  return (
    <div className="space-y-3">
      <span className="block text-sm font-medium text-makhe-ink/80">Student card photo</span>
      <div className="flex flex-wrap gap-2">
        <label className="inline-flex cursor-pointer rounded-full border border-makhe-line bg-makhe-paper px-4 py-2 text-[11px] uppercase tracking-widest text-makhe-ink transition-colors hover:border-makhe-primary">
          Take photo
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={pick}
          />
        </label>
        <label className="inline-flex cursor-pointer rounded-full border border-makhe-line bg-makhe-paper px-4 py-2 text-[11px] uppercase tracking-widest text-makhe-ink transition-colors hover:border-makhe-primary">
          Choose from gallery
          <input type="file" accept="image/*" className="sr-only" onChange={pick} />
        </label>
      </div>
      {file && <span className="block text-xs text-makhe-muted/80">{file.name}</span>}
    </div>
  );
}
